/**
 * O formulário de produto — usado pelo cadastro E pela edição.
 *
 * Ele mora aqui, e não dentro de uma das duas telas, porque as duas editam a
 * MESMA coisa: identificação, preço, grade e fotos. Duplicar o arquivo
 * significaria que "acrescentar campo" passa a ser duas tarefas — e um dia
 * alguém faz uma só.
 *
 * O que muda entre os dois modos é pequeno e explícito:
 *
 *  - no cadastro o CÓDIGO é escolhido; na edição ele é mostrado desabilitado.
 *    Trocá-lo é criar outro produto: ele está impresso na etiqueta de cada peça
 *    no depósito e compõe a URL da ficha (CONSTITUICAO.md §2).
 *  - no cadastro as fotos ficam pendentes até o produto existir (não há a que
 *    anexá-las); na edição elas vão direto para o servidor.
 *
 * A margem calculada aqui é ORIENTAÇÃO. Quem calcula o que vai para o banco é
 * o servidor, sobre os centavos que ele mesmo converteu (CONSTITUICAO.md §1) —
 * a mesma relação do medidor de força de senha com a validação de senha.
 */
import { useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react'

import { Botao } from '../../componentes/Botao'
import { Icone } from '../../componentes/Icone'
import { MensagemDeErro } from '../../componentes/Campos'
import { analisar, formatarMoeda } from '../../plataforma/dinheiro'
import {
  formatarBytes,
  formatarPontos,
  margemPontos,
  CATEGORIAS,
  MAX_FOTOS,
  TIPOS_DE_FOTO,
  type Categoria,
  type Cor,
} from '../../plataforma/produtos'
import {
  juntarCores,
  juntarTamanhos,
  tipoDeGradeDe,
  META_DE_MARGEM,
  PALETA,
  TAMANHOS,
  type ValoresProduto,
} from './valores'

type Props = {
  modo: 'cadastro' | 'edicao'
  inicial: ValoresProduto
  veCusto: boolean
  enviando: boolean
  erro: string
  porCampo: Record<string, string>
  /** Cores do produto que não estão na paleta padrão. Sem elas, editar um
   *  produto de cor "Musgo" apagaria a cor ao salvar. */
  coresExtras?: Cor[]
  /** O mesmo para tamanho fora da lista de partida. */
  tamanhosExtras?: string[]
  placeholderCusto?: string
  placeholderMinimo?: string
  /** Bloco de fotos, montado por cada tela: pendente no cadastro, gravado na
   *  edição. */
  blocoDeFotos?: ReactNode
  /** Caixa informativa do rail. */
  avisoDoRail?: ReactNode
  /** Controles extras acima do botão de salvar (o interruptor ativo/inativo). */
  extrasDoRail?: ReactNode
  rotuloDoBotao: (variacoes: number) => string
  aoEnviar: (valores: ValoresProduto) => void
  aoCancelar: () => void
}

export function FormularioProduto({
  modo,
  inicial,
  veCusto,
  enviando,
  erro,
  porCampo,
  coresExtras = [],
  tamanhosExtras = [],
  placeholderCusto,
  placeholderMinimo,
  blocoDeFotos,
  avisoDoRail,
  extrasDoRail,
  rotuloDoBotao,
  aoEnviar,
  aoCancelar,
}: Props) {
  const [valores, setValores] = useState<ValoresProduto>(inicial)

  // O formulário da edição nasce vazio e é preenchido quando a ficha chega.
  // Sem este efeito, a tela ficaria com os campos em branco depois do
  // carregamento — e salvar apagaria o produto inteiro.
  //
  // A dependência é a ASSINATURA, e não o objeto: `inicial` é recriado a cada
  // render da tela-mãe, e comparar por referência remontaria o formulário a
  // cada tecla — apagando o que a pessoa acabou de digitar.
  const assinatura = JSON.stringify(inicial)
  useEffect(() => {
    setValores(JSON.parse(assinatura) as ValoresProduto)
  }, [assinatura])

  const mudar = <C extends keyof ValoresProduto>(campo: C, valor: ValoresProduto[C]) =>
    setValores((atual) => ({ ...atual, [campo]: valor }))

  const tipoDeGrade = tipoDeGradeDe(valores.categoria)

  // Trocar de categoria troca a grade: numeração de tênis não convive com
  // P..GG. Manter a seleção antiga faria o servidor recusar com "tamanho fora
  // da grade" sem a pessoa entender por quê.
  const gradeAnterior = useRef(tipoDeGrade)
  useEffect(() => {
    if (gradeAnterior.current === tipoDeGrade) return
    gradeAnterior.current = tipoDeGrade
    setValores((atual) => ({ ...atual, tamanhos: [] }))
  }, [tipoDeGrade])

  const paleta = useMemo(() => juntarCores(PALETA, coresExtras), [coresExtras])
  const tamanhosOferecidos = useMemo(
    () => juntarTamanhos(TAMANHOS[tipoDeGrade], tamanhosExtras),
    [tipoDeGrade, tamanhosExtras],
  )

  const precoCentavos = analisar(valores.preco)
  const custoCentavos = veCusto ? analisar(valores.custo) : null

  const margem = useMemo(() => {
    if (precoCentavos === null) return null
    return margemPontos(precoCentavos, custoCentavos ?? 0)
  }, [precoCentavos, custoCentavos])

  const variacoes = valores.tamanhos.length * valores.cores.length
  const pronto =
    valores.nome.trim() !== '' && precoCentavos !== null && precoCentavos > 0 && variacoes > 0

  const alternar = (lista: string[], valor: string) =>
    lista.includes(valor) ? lista.filter((item) => item !== valor) : [...lista, valor]

  return (
    <form
      onSubmit={(evento) => {
        evento.preventDefault()
        if (pronto && !enviando) aoEnviar(valores)
      }}
      className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_340px]"
    >
      <div className="flex min-w-0 flex-col gap-4">
        <Cartao titulo="Identificação">
          <div className="grid gap-3.5 sm:grid-cols-2">
            <CampoTexto
              rotulo="Nome do produto"
              valor={valores.nome}
              aoMudar={(texto) => mudar('nome', texto)}
              erro={porCampo.nome}
              dica="Aparece na etiqueta e no PDV"
              autoFocus
            />
            <CampoTexto
              rotulo="Código base"
              valor={valores.codigo}
              aoMudar={(texto) => mudar('codigo', texto.toUpperCase())}
              erro={porCampo.codigo}
              // Deixar vazio é o caminho normal do cadastro: o servidor deriva
              // do nome e é ele que sabe se o código já existe na loja.
              placeholder={valores.nome ? 'gerado a partir do nome' : 'CAM-PERF'}
              dica={
                modo === 'edicao'
                  ? 'O código não muda: ele está impresso nas etiquetas já emitidas'
                  : 'Vazio: o sistema sugere a partir do nome'
              }
              desabilitado={modo === 'edicao'}
              className="tabular"
            />

            <div>
              <label
                htmlFor="categoria"
                className="mb-1.5 block text-[12.5px] font-semibold text-texto"
              >
                Categoria
              </label>
              <select
                id="categoria"
                value={valores.categoria}
                onChange={(evento) => mudar('categoria', evento.target.value as Categoria)}
                aria-invalid={porCampo.categoria ? true : undefined}
                className="campo"
              >
                {CATEGORIAS.map((item) => (
                  <option key={item.chave} value={item.chave}>
                    {item.rotulo}
                  </option>
                ))}
              </select>
              <p className="mt-1.5 text-[11.5px] text-texto-fraco">
                {valores.categoria === 'tenis'
                  ? 'Tênis usa a grade de numeração da loja'
                  : 'Usa a grade de tamanhos de roupa da loja'}
              </p>
            </div>

            <CampoTexto
              rotulo="Marca"
              valor={valores.marca}
              aoMudar={(texto) => mudar('marca', texto)}
              erro={porCampo.marca}
              placeholder="Lumma Wear"
            />
          </div>
        </Cartao>

        <Cartao
          titulo="Preço e custo"
          dica="A margem é calculada sobre o preço de venda, nunca sobre o custo"
        >
          <div className="grid gap-3.5 sm:grid-cols-3">
            <CampoDinheiro
              rotulo="Preço de venda"
              valor={valores.preco}
              aoMudar={(texto) => mudar('preco', texto)}
              erro={porCampo.preco}
            />
            {veCusto && (
              <CampoDinheiro
                rotulo="Custo"
                valor={valores.custo}
                aoMudar={(texto) => mudar('custo', texto)}
                erro={porCampo.custo}
                placeholder={placeholderCusto}
                dica={
                  modo === 'edicao'
                    ? 'Vazio mantém o custo de cada variação'
                    : undefined
                }
              />
            )}
            {veCusto && <PreviaDeMargem margem={margem} />}
          </div>
        </Cartao>

        <Cartao
          titulo="Grade de variações"
          dica="Cada cruzamento de cor e tamanho gera um SKU com código de barras"
        >
          <fieldset className="mb-5">
            <legend className="mb-2.5 text-[12.5px] font-semibold text-texto">Tamanhos</legend>
            <div className="flex flex-wrap gap-2">
              {tamanhosOferecidos.map((tamanho) => (
                <PillDeGrade
                  key={tamanho}
                  rotulo={tamanho}
                  ativa={valores.tamanhos.includes(tamanho)}
                  aoClicar={() => mudar('tamanhos', alternar(valores.tamanhos, tamanho))}
                />
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="mb-2.5 text-[12.5px] font-semibold text-texto">Cores</legend>
            <div className="flex flex-wrap gap-2">
              {paleta.map((cor) => (
                <PillDeGrade
                  key={cor.nome}
                  rotulo={cor.nome}
                  hex={cor.hex}
                  ativa={valores.cores.includes(cor.nome)}
                  aoClicar={() => mudar('cores', alternar(valores.cores, cor.nome))}
                />
              ))}
            </div>
          </fieldset>

          {porCampo.grade && <MensagemDeErro>{porCampo.grade}</MensagemDeErro>}
          {porCampo.tamanhos && <MensagemDeErro>{porCampo.tamanhos}</MensagemDeErro>}

          <div className="mt-5 border-t border-divisoria pt-4">
            <PreviaDaMatriz tamanhos={valores.tamanhos} cores={valores.cores} paleta={paleta} />
          </div>
        </Cartao>

        {blocoDeFotos}
      </div>

      {/* Rail direita: 340px, `sticky`, com o resumo e o CTA — spec 01. */}
      <div className="flex flex-col gap-4 xl:sticky xl:top-0">
        <div className="rounded-sistema border border-borda bg-superficie p-5 shadow-[var(--shadow-cartao)]">
          <h2 className="mb-3.5 font-titulo text-[15px] font-semibold text-tinta">Resumo</h2>

          <LinhaDeResumo rotulo="Variações">
            {variacoes === 0 ? '—' : `${variacoes} ${variacoes === 1 ? 'SKU' : 'SKUs'}`}
          </LinhaDeResumo>
          <LinhaDeResumo rotulo="Cores × tamanhos">
            {valores.cores.length} × {valores.tamanhos.length}
          </LinhaDeResumo>
          <LinhaDeResumo rotulo="Preço de venda">
            {precoCentavos === null ? '—' : formatarMoeda(precoCentavos)}
          </LinhaDeResumo>
          {veCusto && (
            <LinhaDeResumo rotulo="Custo por peça">
              {custoCentavos === null ? '—' : formatarMoeda(custoCentavos)}
            </LinhaDeResumo>
          )}
          {veCusto && (
            <LinhaDeResumo rotulo="Margem">
              {margem === null ? '—' : formatarPontos(margem)}
            </LinhaDeResumo>
          )}

          {avisoDoRail}

          <div className="mt-4">
            <label
              htmlFor="estoque-minimo"
              className="mb-1.5 block text-[12.5px] font-semibold text-texto"
            >
              Estoque mínimo por tamanho
            </label>
            <div className="composto" aria-invalid={porCampo.estoque_minimo ? true : undefined}>
              <input
                id="estoque-minimo"
                value={valores.minimo}
                inputMode="numeric"
                placeholder={placeholderMinimo}
                onChange={(evento) =>
                  mudar('minimo', evento.target.value.replace(/\D/g, '').slice(0, 3))
                }
                className="tabular font-semibold"
              />
              <span className="text-[13px] text-texto-suave">un</span>
            </div>
            <p className="mt-1.5 text-[11.5px] text-texto-fraco">
              {modo === 'edicao'
                ? 'É o limite que acende o alerta âmbar na grade. Vazio mantém o de cada variação.'
                : 'É o limite que acende o alerta âmbar na grade. Padrão de 2 por tamanho.'}
            </p>
            {porCampo.estoque_minimo && <MensagemDeErro>{porCampo.estoque_minimo}</MensagemDeErro>}
          </div>

          {extrasDoRail}

          {erro && (
            <div
              role="alert"
              className="mt-4 rounded-sistema bg-erro-suave px-4 py-3 text-[13px] font-medium text-erro-texto"
            >
              {erro}
            </div>
          )}

          <button
            type="submit"
            disabled={!pronto || enviando}
            className={`mt-4 flex h-[46px] w-full items-center justify-center gap-2 rounded-sistema text-sm font-bold text-white ${
              pronto && !enviando ? 'bg-tinta' : 'bg-inativo'
            }`}
          >
            <Icone nome="check_circle" tamanho={19} />
            {rotuloDoBotao(variacoes)}
          </button>

          <div className="mt-2.5">
            <Botao variante="secundario" larguraTotal onClick={aoCancelar}>
              Cancelar
            </Botao>
          </div>
        </div>
      </div>
    </form>
  )
}

/* --------------------------------------------------------------- fotos */

/** Uma miniatura da galeria, venha ela do servidor ou da fila de envio. */
export type FotoNaTela = {
  chave: string
  url: string
  nome: string
  tamanho: number
  capa: boolean
  /** Ainda não subiu: a edição já persistiu, o cadastro espera o produto. */
  pendente?: boolean
}

/**
 * O bloco de fotos do formulário.
 *
 * Arrastar OU clicar, miniatura, remover e marcar capa. A área de soltar segue
 * o desenho do protótipo (`Lumma Fit Cadastro.dc.html`): ícone em tint, título,
 * linha de limites e o botão "Escolher arquivo" à direita.
 *
 * O input de arquivo fica visualmente escondido dentro do `label`, e não
 * substituído por um botão com `ref.click()`: assim o teclado alcança o
 * controle nativo, o leitor de tela anuncia "escolher arquivo" e o anel de foco
 * aparece na área inteira via `focus-within`.
 */
export function BlocoDeFotos({
  fotos,
  limite = MAX_FOTOS,
  erro,
  ocupado = false,
  aoEscolher,
  aoRemover,
  aoMarcarCapa,
  rodape,
}: {
  fotos: FotoNaTela[]
  limite?: number
  erro?: string
  ocupado?: boolean
  aoEscolher: (arquivos: File[]) => void
  aoRemover: (foto: FotoNaTela) => void
  aoMarcarCapa: (foto: FotoNaTela) => void
  rodape?: ReactNode
}) {
  const [arrastando, setArrastando] = useState(false)
  const cheio = fotos.length >= limite

  const soltar = (evento: DragEvent<HTMLDivElement>) => {
    evento.preventDefault()
    setArrastando(false)
    if (cheio || ocupado) return
    aoEscolher(Array.from(evento.dataTransfer.files))
  }

  return (
    <Cartao titulo="Fotos" dica="A primeira imagem é a capa: ela aparece na lista e na ficha">
      <div
        onDragOver={(evento) => {
          evento.preventDefault()
          if (!cheio && !ocupado) setArrastando(true)
        }}
        onDragLeave={() => setArrastando(false)}
        onDrop={soltar}
        className={`flex flex-wrap items-center gap-4 rounded-sistema border border-dashed px-5 py-5 focus-within:border-marca focus-within:ring-2 focus-within:ring-marca/15 ${
          arrastando ? 'border-marca bg-marca-suave' : 'border-borda bg-fundo'
        } ${cheio ? 'opacity-60' : ''}`}
      >
        <span className="flex h-[42px] w-12 shrink-0 items-center justify-center rounded-sistema bg-marca-suave text-marca">
          <Icone nome="documento_importar" tamanho={22} />
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-tinta">
            {cheio
              ? `Limite de ${limite} fotos atingido`
              : 'Arraste as fotos ou clique para escolher'}
          </p>
          <p className="mt-[3px] text-[12.5px] text-texto-suave">
            {cheio
              ? 'Remova uma foto para poder enviar outra.'
              : `JPEG ou PNG até 2 MiB · até ${limite} fotos por produto`}
          </p>
        </div>

        <label
          className={`flex h-[34px] shrink-0 items-center gap-2 rounded-sistema border border-borda bg-superficie px-4 text-[13.5px] font-semibold ${
            cheio || ocupado
              ? 'cursor-not-allowed text-inativo'
              : 'cursor-pointer text-texto hover:border-inativo'
          }`}
        >
          <Icone nome="add" tamanho={18} />
          {ocupado ? 'Enviando…' : 'Escolher arquivo'}
          <input
            type="file"
            multiple
            accept={TIPOS_DE_FOTO.join(',')}
            disabled={cheio || ocupado}
            onChange={(evento) => {
              aoEscolher(Array.from(evento.target.files ?? []))
              // Zerar permite reescolher o MESMO arquivo depois de removê-lo:
              // sem isso o `change` não dispara na segunda vez.
              evento.target.value = ''
            }}
            className="sr-only"
          />
        </label>
      </div>

      {erro && <MensagemDeErro>{erro}</MensagemDeErro>}

      {fotos.length > 0 && (
        <ul className="mt-4 grid list-none gap-3 [grid-template-columns:repeat(auto-fill,minmax(150px,1fr))]">
          {fotos.map((foto) => (
            <li
              key={foto.chave}
              className="overflow-hidden rounded-sistema border border-borda bg-superficie"
            >
              <div className="relative flex h-[120px] items-center justify-center bg-divisoria">
                <img
                  src={foto.url}
                  alt={foto.nome}
                  className="h-full w-full object-cover"
                />
                {foto.capa && (
                  <span className="absolute top-2 left-2 rounded-full bg-marca px-2 py-0.5 text-[11px] font-bold text-white">
                    Capa
                  </span>
                )}
                {foto.pendente && (
                  <span className="absolute top-2 right-2 rounded-full bg-atencao-suave px-2 py-0.5 text-[11px] font-bold text-atencao-texto">
                    Ao salvar
                  </span>
                )}
              </div>
              <div className="px-3 py-2">
                <p className="truncate text-[12.5px] font-medium text-tinta" title={foto.nome}>
                  {foto.nome}
                </p>
                <p className="tabular mt-0.5 text-[11.5px] text-texto-suave">
                  {formatarBytes(foto.tamanho)}
                </p>
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    disabled={foto.capa || ocupado}
                    onClick={() => aoMarcarCapa(foto)}
                    className="h-[28px] flex-1 rounded-sistema border border-borda bg-superficie text-[11.5px] font-semibold text-texto hover:border-inativo disabled:cursor-not-allowed disabled:text-inativo disabled:hover:border-borda"
                  >
                    {foto.capa ? 'É a capa' : 'Marcar capa'}
                  </button>
                  <button
                    type="button"
                    disabled={ocupado}
                    onClick={() => aoRemover(foto)}
                    aria-label={`Remover ${foto.nome}`}
                    className="flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-sistema border border-borda bg-superficie text-erro-texto hover:bg-erro-suave disabled:cursor-not-allowed disabled:text-inativo disabled:hover:bg-superficie"
                  >
                    <Icone nome="close" tamanho={15} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {rodape}
    </Cartao>
  )
}

/* -------------------------------------------------------------- pedaços */

export function Cartao({
  titulo,
  dica,
  children,
}: {
  titulo: string
  dica?: string
  children: ReactNode
}) {
  return (
    <section className="rounded-sistema border border-borda bg-superficie p-5 shadow-[var(--shadow-cartao)]">
      <h2 className="font-titulo text-base font-semibold text-tinta">{titulo}</h2>
      {dica && <p className="mt-[3px] mb-4 text-[12.5px] text-texto-suave">{dica}</p>}
      {!dica && <div className="mb-4" />}
      {children}
    </section>
  )
}

/**
 * Campo de texto do sistema.
 *
 * Usa a classe `campo`, que carrega os cinco estados da spec 01 — padrão,
 * hover, foco, erro e desabilitado. O erro liga por `aria-invalid`, então o
 * atributo de acessibilidade e o visual não conseguem divergir.
 */
export function CampoTexto({
  rotulo,
  valor,
  aoMudar,
  erro,
  dica,
  placeholder,
  className = '',
  autoFocus,
  desabilitado,
}: {
  rotulo: string
  valor: string
  aoMudar: (texto: string) => void
  erro?: string
  dica?: string
  placeholder?: string
  className?: string
  autoFocus?: boolean
  desabilitado?: boolean
}) {
  const id = `campo-${rotulo.toLowerCase().replace(/[^a-z]+/g, '-')}`

  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-[12.5px] font-semibold text-texto">
        {rotulo}
      </label>
      <input
        id={id}
        value={valor}
        onChange={(evento) => aoMudar(evento.target.value)}
        placeholder={placeholder}
        aria-invalid={erro ? true : undefined}
        autoFocus={autoFocus}
        disabled={desabilitado}
        className={`campo ${className}`}
      />
      {erro ? (
        <MensagemDeErro>{erro}</MensagemDeErro>
      ) : (
        dica && <p className="mt-1.5 text-[11.5px] text-texto-fraco">{dica}</p>
      )}
    </div>
  )
}

/**
 * Campo de dinheiro: `composto` com o prefixo R$.
 *
 * O invólucro carrega a borda e o anel de foco via `:focus-within` — o input
 * de dentro não tem borda para colorir. É a classe que existe exatamente para
 * este caso (prefixo, sufixo, ícone).
 */
export function CampoDinheiro({
  rotulo,
  valor,
  aoMudar,
  erro,
  dica,
  placeholder = '0,00',
}: {
  rotulo: string
  valor: string
  aoMudar: (texto: string) => void
  erro?: string
  dica?: string
  placeholder?: string
}) {
  const id = `dinheiro-${rotulo.toLowerCase().replace(/[^a-z]+/g, '-')}`
  // Texto que não é valor monetário acende o campo antes de enviar. Não
  // substitui a validação do servidor — é orientação, e o servidor recusa de
  // novo se passar.
  const invalido = Boolean(erro) || (valor.trim() !== '' && analisar(valor) === null)

  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-[12.5px] font-semibold text-texto">
        {rotulo}
      </label>
      <div className="composto" aria-invalid={invalido ? true : undefined}>
        <span className="text-sm text-texto-suave">R$</span>
        <input
          id={id}
          value={valor}
          inputMode="decimal"
          placeholder={placeholder}
          onChange={(evento) => aoMudar(evento.target.value)}
          className="tabular"
        />
      </div>
      {erro ? (
        <MensagemDeErro>{erro}</MensagemDeErro>
      ) : invalido ? (
        <MensagemDeErro>Informe um valor como 189,90.</MensagemDeErro>
      ) : (
        dica && <p className="mt-1.5 text-[11.5px] text-texto-fraco">{dica}</p>
      )}
    </div>
  )
}

/** Prévia da margem ao vivo, com o aviso de meta da spec 05. */
export function PreviaDeMargem({ margem }: { margem: number | null }) {
  if (margem === null) {
    return (
      <div className="rounded-sistema border border-borda bg-fundo px-3.5 py-3">
        <p className="mb-1 text-[11.5px] text-texto-suave">Margem</p>
        <p className="tabular text-base font-bold text-texto-fraco">—</p>
      </div>
    )
  }

  const abaixo = margem < META_DE_MARGEM

  return (
    <div
      className={`rounded-sistema border px-3.5 py-3 ${
        abaixo ? 'border-atencao-suave bg-atencao-fundo' : 'border-ok-suave bg-ok-fundo'
      }`}
    >
      <p className="mb-1 text-[11.5px] text-texto-suave">Margem</p>
      <p className={`tabular text-base font-bold ${abaixo ? 'text-atencao-texto' : 'text-ok'}`}>
        {formatarPontos(margem)}
      </p>
      {abaixo && (
        <p className="mt-1 text-[11.5px] leading-[1.45] text-atencao-texto">
          Abaixo da meta de 45% do vestuário. Não impede o cadastro.
        </p>
      )}
    </div>
  )
}

/** Pill de seleção de tamanho ou cor — o controle da spec 01. */
export function PillDeGrade({
  rotulo,
  hex,
  ativa,
  aoClicar,
}: {
  rotulo: string
  hex?: string
  ativa: boolean
  aoClicar: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={ativa}
      onClick={aoClicar}
      className={`inline-flex min-w-[52px] items-center justify-center gap-2 rounded-full border px-3.5 py-2 text-[13.5px] font-semibold ${
        ativa
          ? 'border-marca bg-marca-suave text-marca-forte'
          : 'border-borda bg-superficie text-tinta hover:border-inativo'
      }`}
    >
      {hex && (
        <span
          aria-hidden
          style={{ background: hex }}
          className="h-4 w-4 shrink-0 rounded-full border border-[rgba(0,0,0,0.12)]"
        />
      )}
      {rotulo}
    </button>
  )
}

/**
 * Prévia da matriz que vai ser gerada.
 *
 * Não é um campo de estoque inicial como no protótipo — é a confirmação visual
 * de quantas e quais variações o botão vai criar. O lojista confere grade
 * olhando, e a ordem é a mesma que o servidor gera (cor externa, tamanho
 * interno).
 */
export function PreviaDaMatriz({
  tamanhos,
  cores,
  paleta,
}: {
  tamanhos: string[]
  cores: string[]
  paleta: Cor[]
}) {
  if (tamanhos.length === 0 || cores.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-6 text-center">
        <Icone nome="grid_view" tamanho={24} className="text-inativo" />
        <p className="text-[13.5px] text-texto-suave">Escolha ao menos um tamanho e uma cor</p>
      </div>
    )
  }

  return (
    <>
      <p className="mb-3 text-[12.5px] text-texto-suave">
        Serão gerados{' '}
        <strong className="tabular font-semibold text-tinta">{tamanhos.length * cores.length}</strong>{' '}
        SKUs, um por cruzamento, cada um com código de barras próprio.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <caption className="sr-only">Variações que serão criadas</caption>
          <thead>
            <tr>
              <th scope="col" className="pb-2 text-left text-xs font-semibold text-texto-suave">
                Cor
              </th>
              {tamanhos.map((tamanho) => (
                <th
                  key={tamanho}
                  scope="col"
                  className="min-w-[44px] pb-2 text-center text-xs font-semibold text-texto-suave"
                >
                  {tamanho}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cores.map((cor) => (
              <tr key={cor}>
                <th
                  scope="row"
                  className="border-t border-divisoria py-2 text-left text-[13px] font-medium text-tinta"
                >
                  <span className="flex items-center gap-2">
                    <span
                      aria-hidden
                      style={{ background: paleta.find((item) => item.nome === cor)?.hex }}
                      className="h-3.5 w-3.5 shrink-0 rounded-full border border-[rgba(0,0,0,0.12)]"
                    />
                    {cor}
                  </span>
                </th>
                {tamanhos.map((tamanho) => (
                  <td
                    key={tamanho}
                    className="border-t border-divisoria py-2 text-center text-inativo"
                  >
                    <Icone nome="check" tamanho={15} className="text-marca" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

export function LinhaDeResumo({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-3 border-t border-divisoria py-2.5 text-[13px] first:border-t-0">
      {/* Regra 2 do "layout que já custou bug": o número não quebra, o rótulo
          ao lado é que encolhe. */}
      <span className="min-w-0 flex-auto text-texto-suave">{rotulo}</span>
      <span className="tabular shrink-0 text-right font-semibold whitespace-nowrap text-tinta">
        {children}
      </span>
    </div>
  )
}

